import type {
  ChoiceAnswer,
  DragMatchingImageAnswer,
  DragMatchingTextAnswer,
  FillBlanksAnswer,
  OddOutAnswer,
  OrderAnswer,
  QuestionAnswer,
  SlideElement,
  SpeakingAnswer,
  SpellingAnswer,
} from "@ai-teach/types";
import { getRevealedIndexes } from "../utils/spellingReveal";

/**
 * Validates a single question answer
 * Returns true if the answer is correct, false otherwise
 */
export function validateQuestionAnswer(
  element: SlideElement,
  answer: QuestionAnswer | undefined
): boolean {
  switch (element.type) {
    case "textChoice":
    case "imageChoice":
      // For choice elements, undefined answer means "not selected"
      // This is valid and checked in validateChoiceAnswer
      return validateChoiceAnswer(element, answer as ChoiceAnswer | undefined);

    case "oddTextOut":
    case "oddImageOut":
      // For odd-out questions, must have an answer
      if (!answer) return false;
      return validateOddOutAnswer(element, answer as OddOutAnswer);

    case "fillBlanks":
      // For fill-blanks questions, must have an answer
      if (!answer) return false;
      return validateFillBlanksAnswer(element, answer as FillBlanksAnswer);

    case "dragMatchingText":
      // For drag-matching questions, must have an answer
      if (!answer) return false;
      return validateDragMatchingTextAnswer(element, answer as DragMatchingTextAnswer);

    case "dragMatchingImage":
      // For drag-matching questions, must have an answer
      if (!answer) return false;
      return validateDragMatchingImageAnswer(element, answer as DragMatchingImageAnswer);

    case "orderImage":
    case "orderText":
      if (!answer) return false;
      return validateOrderAnswer(element, answer as OrderAnswer);

    case "spelling":
      // For spelling questions, must have an answer
      if (!answer) return false;
      return validateSpellingAnswer(element, answer as SpellingAnswer);

    case "speaking":
      // For speaking questions, must have an answer
      if (!answer) return false;
      return validateSpeakingAnswer(element, answer as SpeakingAnswer);

    default:
      return false;
  }
}

/**
 * Validates TextChoice or ImageChoice answer
 *
 * For each element (individual choice):
 * - If isCorrectAnswer=true: Must be selected
 * - If isCorrectAnswer=false: Must NOT be selected
 *
 * Scoring counts ALL elements, so each choice is validated independently
 */
function validateChoiceAnswer(
  element: Extract<SlideElement, { type: "textChoice" | "imageChoice" }>,
  answer: ChoiceAnswer | undefined
): boolean {
  const selectedIds = answer?.selectedIds || [];
  const isSelected = selectedIds.includes(element.id);

  // Correct choice: must be selected
  // Wrong choice: must NOT be selected
  const isCorrect = element.isCorrectAnswer ? isSelected : !isSelected;

  return isCorrect;
}

/**
 * Validates OddTextOut or OddImageOut answer
 * The selected choice must have isCorrectAnswer = true
 */
function validateOddOutAnswer(
  element: Extract<SlideElement, { type: "oddTextOut" | "oddImageOut" }>,
  answer: OddOutAnswer
): boolean {
  const selectedId = answer.selectedId;

  // No selection = wrong
  if (!selectedId) return false;

  // Parse selected index
  const selectedIndex = Number.parseInt(selectedId, 10);
  if (Number.isNaN(selectedIndex)) {
    return false; // Invalid ID format
  }

  if (element.type === "oddTextOut") {
    const selectedChoice = element.textChoices?.[selectedIndex];
    return selectedChoice?.isCorrectAnswer === true;
  }

  if (element.type === "oddImageOut") {
    const selectedChoice = element.imageChoices?.[selectedIndex];
    return selectedChoice?.isCorrectAnswer === true;
  }

  return false;
}

/**
 * Validates FillBlanks answer
 * All blanks must be filled with correct options
 */
function validateFillBlanksAnswer(
  element: Extract<SlideElement, { type: "fillBlanks" }>,
  answer: FillBlanksAnswer
): boolean {
  const answers = answer.answers || {};
  const blocks = element.blocks || [];

  // Must answer all blanks
  if (Object.keys(answers).length !== blocks.length) return false;

  // Check each blank
  for (let i = 0; i < blocks.length; i++) {
    const blankId = `${i}`;
    const selectedOptionId = answers[blankId];

    // Blank not answered = wrong
    if (selectedOptionId === undefined) return false;

    if (element.answerMode === "type") {
      // type mode: stored string is the typed text; compare against options[0].text exactly
      if (selectedOptionId !== blocks[i]?.options[0]?.text) return false;
      continue;
    }

    const selectedOptionIndex = Number.parseInt(selectedOptionId, 10);
    if (Number.isNaN(selectedOptionIndex)) {
      return false; // Invalid option ID format
    }
    const selectedOption = blocks[i]?.options[selectedOptionIndex];

    // Selected option not correct = wrong
    if (selectedOption?.isCorrectAnswer !== true) return false;
  }

  return true;
}

/**
 * Validates DragMatchingText answer
 * The matched text choice must have isCorrectAnswer = true
 */
function validateDragMatchingTextAnswer(
  element: Extract<SlideElement, { type: "dragMatchingText" }>,
  answer: DragMatchingTextAnswer
): boolean {
  const matches = answer.matches || {};

  // Must have exactly one match (image -> text)
  const matchedTextId = matches.image;
  if (!matchedTextId) return false;

  const matchedTextIndex = Number.parseInt(matchedTextId, 10);
  if (Number.isNaN(matchedTextIndex)) {
    return false; // Invalid match ID format
  }
  const matchedChoice = element.textChoices?.[matchedTextIndex];

  return matchedChoice?.isCorrectAnswer === true;
}

/**
 * Validates Order answer (orderImage / orderText)
 * For orderImage: correct order is determined by item.order field (teacher-set via "設定答案").
 * Falls back to array order for backward compat when no item.order is set.
 * For orderText: correct order is always array order (no order field on items).
 */
/** The sequence the student had to produce: the teacher's explicit `order` when every image has
 *  one, the authoring order otherwise. */
export function getCorrectOrderIds(
  element: Extract<SlideElement, { type: "orderImage" | "orderText" }>
): string[] {
  if (element.type === "orderImage" && element.items.every((it) => it.order !== undefined)) {
    return [...element.items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((it) => it.id);
  }
  return element.items.map((it) => it.id);
}

function validateOrderAnswer(
  element: Extract<SlideElement, { type: "orderImage" | "orderText" }>,
  answer: OrderAnswer
): boolean {
  const correctOrder = getCorrectOrderIds(element);
  const studentOrder = answer.order;
  if (studentOrder.length !== correctOrder.length) return false;
  return correctOrder.every((id, i) => studentOrder[i] === id);
}

/**
 * Validates DragMatchingImage answer
 * The matched image choice must have isCorrectAnswer = true
 */
function validateDragMatchingImageAnswer(
  element: Extract<SlideElement, { type: "dragMatchingImage" }>,
  answer: DragMatchingImageAnswer
): boolean {
  const matches = answer.matches || {};

  // Must have exactly one match (text -> image)
  const matchedImageId = matches.text;
  if (!matchedImageId) return false;

  const matchedImageIndex = Number.parseInt(matchedImageId, 10);
  if (Number.isNaN(matchedImageIndex)) {
    return false; // Invalid match ID format
  }
  const matchedChoice = element.imageChoices?.[matchedImageIndex];

  return matchedChoice?.isCorrectAnswer === true;
}

/**
 * Validates Spelling answer (all-or-nothing)
 * Every student-editable letter (i.e. every letter that isn't revealed) must match the target
 * word exactly — comparison is case-sensitive by design, never lowercase either side.
 * A word with no editable letters (e.g. a one-letter word with the hint on) is vacuously correct,
 * matching the same "nothing to grade" convention used elsewhere (e.g. empty FillBlanks blocks).
 */
function validateSpellingAnswer(
  element: Extract<SlideElement, { type: "spelling" }>,
  answer: SpellingAnswer
): boolean {
  const word = element.word;
  const letters = answer.letters || {};
  const revealedIndexes = getRevealedIndexes(element);

  for (let i = 0; i < word.length; i++) {
    if (revealedIndexes.has(i)) continue;
    if (letters[`${i}`] !== word[i]) return false;
  }

  return true;
}

/**
 * Validates Speaking answer (all-or-nothing)
 * Every sentence must have a chosen option, and every chosen option must be the correct one.
 * Mirrors validateFillBlanksAnswer's index-keyed shape, minus the "type" answer mode (speaking
 * is always multiple-choice).
 */
function validateSpeakingAnswer(
  element: Extract<SlideElement, { type: "speaking" }>,
  answer: SpeakingAnswer
): boolean {
  const answers = answer.answers || {};
  const sentences = element.sentences || [];

  // Must answer every sentence
  if (Object.keys(answers).length !== sentences.length) return false;

  for (let i = 0; i < sentences.length; i++) {
    const selectedOptionId = answers[`${i}`];

    // Sentence not answered = wrong
    if (selectedOptionId === undefined) return false;

    const selectedOptionIndex = Number.parseInt(selectedOptionId, 10);
    if (Number.isNaN(selectedOptionIndex)) {
      return false; // Invalid option ID format
    }
    const selectedOption = sentences[i]?.options[selectedOptionIndex];

    // Selected option not correct = wrong
    if (selectedOption?.isCorrectAnswer !== true) return false;
  }

  return true;
}
